/**
 * Status chip (docs/04 §3-§4). The status prop is typed against the canonical
 * list generated from docs/04 §4 — a status not in the doc is a compile error,
 * not a review catch. Color is always paired with the text (never color alone,
 * docs/03 §8).
 */

import { STATUSES, type StatusKey } from '@propos/shared';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

const FAMILY_CLASSES: Record<string, string> = {
  ok: 'bg-ok-bg text-ok-fg',
  neutral: 'bg-neut-bg text-neut-fg',
  info: 'bg-info-bg text-info-fg',
  amber: 'bg-amber-bg text-amber-fg',
  red: 'bg-red-bg text-red-fg',
  gold: 'bg-gold-tint text-gold-text',
};

export function Chip({ status }: { readonly status: StatusKey }): ReactNode {
  const definition = STATUSES[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-chip font-semibold',
        FAMILY_CLASSES[definition.family],
      )}
    >
      {definition.label}
    </span>
  );
}

/** Count pill (docs/04 §3) — My Work counts, list totals. */
export function CountPill({
  count,
  label,
}: {
  readonly count: number;
  readonly label: string;
}): ReactNode {
  return (
    <span
      aria-label={`${String(count)} ${label}`}
      className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand-700 px-1.5 text-chip font-semibold text-on-brand"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
